import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { coreAPI, ExchangeRateRow, MonthlyExchangeRates } from '../../lib/api';
import type { Company } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters, usePreferences } from '../../contexts/PreferencesContext';
import { useView } from '../../contexts/ViewContext';
import { usePagination, getPageNumbers } from '../../hooks/usePagination';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Coins, Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const ALL = 'ALL';
const PLATFORM = 'platform';
const FOLLOW_VIEW = 'view';
type ViewMode = 'daily' | 'monthly';

/** Small rates (1 INR = 0.0106 USD) need more places than large ones (1 USD = 94.30 INR). */
function fmtRate(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(n < 1 ? 6 : 4);
}

/** 'YYYY-MM' -> 'Aug 2026' */
function formatMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

interface LatestRates {
  base_currency: string;
  rates: Record<string, string>;
  as_of: string;
}

/**
 * Read-only view of the exchange rates Helix holds.
 *
 * Staff and superusers only. Every rate is stored relative to GBP (the platform
 * base currency): 1 GBP = `rate` units of the row's currency. That is said on
 * the page because it is the single most common misreading of this table —
 * an INR row of 106.5 is GBP→INR, not INR→GBP.
 */
interface ExchangeRatesPageProps {
  /**
   * Rendered as a section inside Admin Setup rather than as its own route.
   * Drops the page padding and the h1, which the host page already provides —
   * without it the tab shows a second title and doubled padding.
   */
  embedded?: boolean;
}

export default function ExchangeRatesPage({ embedded = false }: ExchangeRatesPageProps = {}) {
  const { user } = useAuth();
  const { formatDate } = useFormatters();
  const { preferences } = usePreferences();
  const { selectedCompanyId } = useView();
  // Staff can point the page at any customer, or at the platform-wide rows;
  // by default it follows whatever the view switcher has selected.
  const [picked, setPicked] = useState<string>(FOLLOW_VIEW);
  const [companies, setCompanies] = useState<Company[]>([]);
  const pickedCompany: Company | null =
    picked !== FOLLOW_VIEW && picked !== PLATFORM ? companies.find(c => String(c.id) === picked) ?? null : null;
  const companyParam: number | 'platform' | undefined =
    picked === PLATFORM ? 'platform'
    : picked === FOLLOW_VIEW ? (selectedCompanyId ?? undefined)
    : Number(picked);
  // The base everything is shown in: the picked company's own currency, the
  // platform base when looking at platform-wide rows, else the view's currency.
  // GBP is only how rates are STORED.
  const baseCurrency =
    picked === PLATFORM ? 'GBP'
    : pickedCompany?.base_currency || preferences.currency || 'GBP';
  const isStaff = !!user?.is_staff || !!user?.is_superuser;
  // Company admins reach this page from Admin Setup; the backend pins them
  // to their own company's rates, and the company picker below is
  // staff-only, so they simply see their own tenant.
  const canView = isStaff || !!user?.is_company_admin;

  const [latest, setLatest] = useState<LatestRates | null>(null);
  const [rows, setRows] = useState<ExchangeRateRow[]>([]);
  const [view, setView] = useState<ViewMode>('monthly');
  const [monthly, setMonthly] = useState<MonthlyExchangeRates | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>(ALL);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const pagination = usePagination({ pageSize: 50 });
  const { currentPage, pageSize, setFromResponse, goToPage } = pagination;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        currency: currency === ALL ? undefined : currency,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        base: baseCurrency,
        company: companyParam,
      };
      const [latestRes, listRes, monthlyRes] = await Promise.all([
        coreAPI.getLatestExchangeRates({ company: companyParam }),
        view === 'daily'
          ? coreAPI.listExchangeRates({ ...filters, page: currentPage, page_size: pageSize })
          : null,
        view === 'monthly' ? coreAPI.getMonthlyExchangeRates({ ...filters, months: 60 }) : null,
      ]);
      setLatest(latestRes.data);
      if (listRes) {
        setRows(listRes.data.results);
        setFromResponse(listRes.data.count, (listRes.data.results || []).length);
      }
      if (monthlyRes) setMonthly(monthlyRes.data);
    } catch (err: unknown) {
      console.error('Failed to load exchange rates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load exchange rates');
    } finally {
      setLoading(false);
    }
  }, [view, currency, dateFrom, dateTo, currentPage, pageSize, setFromResponse, baseCurrency, companyParam]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  useEffect(() => {
    if (!isStaff) return;
    coreAPI.getCompanies({ company_type: 'customer', is_active: true, page_size: 200 })
      .then(res => setCompanies((res.data.results ?? []).slice().sort((a, b) => a.name.localeCompare(b.name))))
      .catch((err: unknown) => console.error('Failed to load companies for the rate picker:', err));
  }, [canView]);

  if (!canView) {
    // Hidden from the sidebar for everyone else; a typed URL lands here.
    return <Navigate to="/dashboard" replace />;
  }

  // Latest, re-based client-side. Stored is "1 GBP = X"; base-first is
  // stored(X) / stored(BASE) ("1 BASE = N X"); the quoted form is its inverse.
  const storedBase = latest ? Number(latest.rates[baseCurrency] ?? (baseCurrency === 'GBP' ? 1 : NaN)) : NaN;
  const latestInBase = (code: string): number | null => {
    if (!latest) return null;
    if (code === baseCurrency) return 1;
    const x = code === 'GBP' ? 1 : Number(latest.rates[code]);
    if (!Number.isFinite(storedBase) || !storedBase || !x) return null;
    return x / storedBase;
  };
  const currencies = latest
    ? Object.keys(latest.rates).filter(c => c !== baseCurrency).sort()
    : [];

  const monthlyCols = 1 + (monthly?.currencies.length ?? 0);
  const showingPlatformWide = monthly ? monthly.company === null : picked === PLATFORM;
  const scopesSeen = new Set<string>();
  monthly?.months.forEach(m => Object.values(monthly.rates[m] ?? {}).forEach(c => scopesSeen.add(c.scope)));
  const mixedScopes = scopesSeen.size > 1;
  // A base the store has no rate for cannot express anything; say so instead of
  // rendering a wall of dashes.
  const baseHasRate = baseCurrency === 'GBP' || (latest ? Object.prototype.hasOwnProperty.call(latest.rates, baseCurrency) : true);

  const onFilterChange = (apply: () => void) => {
    apply();
    goToPage(1);
  };

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      <div className="flex items-start justify-between gap-4">
        <div>
          {!embedded && (
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Coins className="h-6 w-6 text-primary" />
              Exchange Rates
            </h1>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            Read-only. Shown base-first as <span className="font-mono">1 {baseCurrency} = N currency</span>, with the
            quoted form (<span className="font-mono">1 currency = N {baseCurrency}</span>) alongside — {baseCurrency} is
            {monthly?.company_name ? <> the base currency of <span className="font-medium">{monthly.company_name}</span></> : ' the platform base'}.
            A customer's own rate is used wherever it has one; the platform rate fills the rest.
            These are the rates behind every converted figure in Helix — spend, converted unit
            prices, group-currency values — so a stale or placeholder rate here is a stale
            converted number everywhere.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isStaff && (
            <Select value={picked} onValueChange={v => { setPicked(v); goToPage(1); }}>
              <SelectTrigger className="w-[260px]" aria-label="Rates for">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FOLLOW_VIEW}>Follow the selected view</SelectItem>
                <SelectItem value={PLATFORM}>Platform-wide rates (fallback)</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}{c.base_currency ? ` · ${c.base_currency}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {showingPlatformWide && (
        <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
          Showing the <span className="font-medium">platform-wide</span> rates — the fallback every customer
          without its own rate converts through. Pick a company above to see its own rates (shown in its
          own base currency).
        </div>
      )}

      {!baseHasRate && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4" />
          No {baseCurrency} rate is stored, so nothing can be expressed in {baseCurrency}. Add a {baseCurrency}
          rate, or view the platform-wide rates in GBP.
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Latest rate per currency */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Latest rates</span>
            {latest && (
              <span className="text-xs font-normal text-muted-foreground">
                as of {formatDate(latest.as_of)}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!latest && loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : currencies.length === 0 ? (
            <p className="text-sm text-muted-foreground">No exchange rates stored yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {currencies.map(code => {
                const v = latestInBase(code);
                return (
                  <div key={code} className="rounded-lg border bg-muted/30 p-3" title={`stored: 1 GBP = ${latest!.rates[code] ?? '1'} ${code}`}>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">1 {baseCurrency} =</div>
                    <div className="text-lg font-semibold font-mono">{fmtRate(v)} <span className="text-sm font-normal text-muted-foreground">{code}</span></div>
                    <div className="text-xs text-muted-foreground">1 {code} = {v ? fmtRate(1 / v) : '—'} {baseCurrency}</div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Rate history</span>
            <div className="flex items-center gap-1" role="tablist" aria-label="History view">
              <Button size="sm" variant={view === 'monthly' ? 'default' : 'outline'}
                      role="tab" aria-selected={view === 'monthly'}
                      onClick={() => setView('monthly')}>Monthly</Button>
              <Button size="sm" variant={view === 'daily' ? 'default' : 'outline'}
                      role="tab" aria-selected={view === 'daily'}
                      onClick={() => setView('daily')}>All rates</Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="xr-currency">Currency</label>
              <Select value={currency} onValueChange={v => onFilterChange(() => setCurrency(v))}>
                <SelectTrigger id="xr-currency" className="w-[160px]">
                  <SelectValue placeholder="All currencies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All currencies</SelectItem>
                  {currencies.map(code => (
                    <SelectItem key={code} value={code}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="xr-from">From</label>
              <Input id="xr-from" type="date" value={dateFrom} className="w-[160px]"
                     onChange={e => onFilterChange(() => setDateFrom(e.target.value))} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="xr-to">To</label>
              <Input id="xr-to" type="date" value={dateTo} className="w-[160px]"
                     onChange={e => onFilterChange(() => setDateTo(e.target.value))} />
            </div>
            {(currency !== ALL || dateFrom || dateTo) && (
              <Button variant="ghost" size="sm"
                      onClick={() => onFilterChange(() => { setCurrency(ALL); setDateFrom(''); setDateTo(''); })}>
                Clear
              </Button>
            )}
            <div className="ml-auto text-sm text-muted-foreground">
              {view === 'daily'
                ? `${pagination.totalCount.toLocaleString()} rate${pagination.totalCount === 1 ? '' : 's'}`
                : monthly ? `${monthly.months.length} month${monthly.months.length === 1 ? '' : 's'} · last rate of each month` : ''}
            </div>
          </div>

          {view === 'monthly' && (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    {(monthly?.currencies ?? []).map(code => (
                      <TableHead key={code} className="text-right">1 {baseCurrency} = <span className="font-semibold">{code}</span></TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && !monthly ? (
                    <TableRow>
                      <TableCell colSpan={monthlyCols} className="text-center text-muted-foreground py-8">
                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…
                      </TableCell>
                    </TableRow>
                  ) : !monthly || monthly.months.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={monthlyCols} className="text-center text-muted-foreground py-8">
                        No rates match these filters.
                      </TableCell>
                    </TableRow>
                  ) : monthly.months.map(month => (
                    <TableRow key={month} data-testid="xr-month-row">
                      <TableCell className="font-mono text-sm font-medium">{formatMonth(month)}</TableCell>
                      {monthly.currencies.map(code => {
                        const cell = monthly.rates[month]?.[code];
                        return (
                          <TableCell key={code} className="text-right font-mono"
                                     title={cell ? `${cell.scope === 'customer' ? 'customer rate' : 'platform rate'} of ${formatDate(cell.rate_date)} · stored 1 GBP = ${cell.rate} ${code}` : 'no rate this month'}>
                            {cell?.rate_in_base ? (
                              <>
                                <div>{fmtRate(cell.rate_in_base)}</div>
                                <div className="text-[11px] text-muted-foreground">1 {code} = {fmtRate(cell.inverse_in_base)} {baseCurrency}</div>
                              </>
                            ) : <span className="text-muted-foreground">—</span>}
                            {mixedScopes && cell?.scope === 'platform' && <span className="ml-1 text-[10px] text-muted-foreground align-top" title="platform rate">P</span>}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {view === 'daily' && (<>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead className="text-right">1 {baseCurrency} = currency</TableHead>
                  <TableHead className="text-right">1 currency = {baseCurrency}</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Fetched</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No rates match these filters.
                    </TableCell>
                  </TableRow>
                ) : rows.map(r => (
                  <TableRow key={r.id} data-testid="xr-row">
                    <TableCell className="font-mono text-sm">{formatDate(r.rate_date)}</TableCell>
                    <TableCell>
                      <span className="font-medium">{r.currency}</span>
                      {r.currency_display && r.currency_display !== r.currency && (
                        <span className="text-muted-foreground text-xs ml-2">{r.currency_display}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono" title={`stored: 1 GBP = ${r.rate} ${r.currency}`}>
                      {fmtRate(r.rate_in_base)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {fmtRate(r.inverse_in_base)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.scope === 'customer' ? 'default' : 'outline'}>
                        {r.scope === 'customer' ? (r.company_name || 'customer') : 'platform'}
                      </Badge>
                    </TableCell>
                    <TableCell><Badge variant="outline">{r.source}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(r.fetched_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pagination.hasMultiplePages && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={pagination.prevPage} disabled={pagination.isFirstPage}>Previous</Button>
                {getPageNumbers(pagination.currentPage, pagination.totalPages).map(n => (
                  <Button key={n} variant={n === pagination.currentPage ? 'default' : 'outline'} size="sm"
                          onClick={() => goToPage(n)}>{n}</Button>
                ))}
                <Button variant="outline" size="sm" onClick={pagination.nextPage} disabled={pagination.isLastPage}>Next</Button>
              </div>
            </div>
          )}
          </>)}
        </CardContent>
      </Card>
    </div>
  );
}
